import { computed, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import type { CountPerRating, ReviewStatisticsObject } from '~/modules/reviews/types';

/**
 * This store is responsible for holding and computing review statistics,
 * such as average rating, rating distribution, and total counts.
 */

export default defineStore('reviewsStatisticsStore', () => {
  // state
  const statistics = shallowRef<ReviewStatisticsObject | null>(null);

  // getters
  const isReady = computed(() => !!statistics.value);
  const averageRating = computed(() => statistics.value?.averageRating || null);
  const totalCount = computed(() => statistics.value?.totalCount || null);

  /**
   * This computed property formats the rating distribution data.
   * It ensures that there is always an entry for each rating from 5 to 1,
   * even if the API response does not include a count for a specific rating.
   */
  const countPerRating = computed<CountPerRating[]>(() => {
    if (!statistics.value?.countPerRating) return [];

    return [5, 4, 3, 2, 1].map((rating) => {
      const ratingData = statistics.value?.countPerRating.find((x) => x.rating === rating);
      const { count = 0, percentage = 0 } = ratingData ?? {};

      return { rating, count, percentage };
    });
  });
  const sliders = computed(() => statistics.value?.sliders || []);

  return {
    // state
    statistics,
    // getters
    isReady,
    averageRating,
    totalCount,
    countPerRating,
    sliders,
  };
});
